import { useNavigate } from "react-router-dom";
import EditableEngagementTitle from "./engagements-list/components/engagement-basic-list/components/EditableEngagementTitle.tsx";
import { EngagementStatusBadge } from "./EngagementStatusBadge.tsx";
import { Engagement } from "@models/engagement.model.ts";
import { Paths } from "src/paths";
import "./engagementHeader.scss";
import HistoryButton from "@/shared/components/versions/HistoryButton";
import { RecordId } from "@/shared/components/recordId/RecordId";

interface EngagementHeaderProps {
  engagement: Engagement;
  engagementName: string;
  editingEngagementName: boolean;
  onEngagementNameChange: (value: string) => void;
  onSubmitEngagementName: () => void;
  onStartEditName: () => void;
  onCancelEditName: () => void;
  onBack: () => void;
  /** Opens the config history drawer (MPN-1334). Omitted, no button renders. */
  onOpenHistory?: () => void;
  /** Config versions this engagement has. Omitted means no badge — never a zero. */
  historyCount?: number;
}

const TYPE_LABEL: Record<string, string> = {
  REACH: "Reach",
  CREATE: "Create",
};

export const EngagementHeader = ({
  engagement,
  engagementName,
  editingEngagementName,
  onEngagementNameChange,
  onSubmitEngagementName,
  onStartEditName,
  onCancelEditName,
  onBack,
  onOpenHistory,
  historyCount,
}: EngagementHeaderProps) => {
  const navigate = useNavigate();
  const orgName = engagement.client?.name;
  const orgId = engagement.client?.id;
  const canOpenOrg = !!orgId && orgId > 0;

  const typeLabel = TYPE_LABEL[engagement.type] ?? engagement.type;

  // No Created stamp (MPN-1530 §1.3). It was the engagement record's creation
  // timestamp, which says nothing about the campaign being monitored: a campaign
  // set up in March and flighted in July read "Created 4 Mar". The Monitor header
  // now shows the flight itself, MIN/MAX of the scheduled ad groups, which is the
  // window the numbers underneath it actually cover.

  return (
    <div className="engagement-header">
      <div className="engagement-header__left">
        <button
          type="button"
          className="engagement-header__back"
          onClick={onBack}
          aria-label="Back to engagements"
          title="Back to engagements"
        >
          <i className="bi bi-arrow-left" />
        </button>

        <div className="engagement-header__title">
          <EditableEngagementTitle
            label=""
            isReadOnly={!editingEngagementName}
            value={engagementName}
            onChange={(e) => onEngagementNameChange(e.target.value)}
            onSubmit={onSubmitEngagementName}
            onDoubleClick={onStartEditName}
            onCancel={onCancelEditName}
            variant="standard"
          />
          <i
            className={`engagement-header__pencil bi ${editingEngagementName ? "bi-check" : "bi-pencil"}`}
            onClick={editingEngagementName ? onSubmitEngagementName : onStartEditName}
            role="button"
            aria-label={editingEngagementName ? "Save name" : "Rename engagement"}
            title={editingEngagementName ? "Save name" : "Rename engagement"}
          />
        </div>

        <div className="engagement-header__meta">
          {orgName && (
            <button
              type="button"
              className="engagement-header__client"
              onClick={canOpenOrg ? () => navigate(`${import.meta.env.BASE_URL}${Paths.ORGANIZATION_DETAIL}/${orgId}`) : undefined}
              disabled={!canOpenOrg}
              title={canOpenOrg ? `Open ${orgName}` : orgName}
            >
              <i className="bi bi-building" />
              {orgName}
            </button>
          )}
          {typeLabel && <span className="engagement-header__type">{typeLabel}</span>}
          <RecordId label="Engagement" id={engagement.id} />
        </div>
      </div>

      <div className="engagement-header__right">
        {onOpenHistory && (
          <HistoryButton
            onClick={onOpenHistory}
            title="Config change history"
            count={historyCount}
          />
        )}
        <EngagementStatusBadge engagement={engagement as any} />
      </div>
    </div>
  );
};
